import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { SchemaOrg } from '@/components/base/SchemaOrg';
import { movies } from '@/mocks/movies';
import { categories, categoryNameMap, categoryTitleMap, categoryDescMap } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'hot' | 'new' | 'rating'>('hot');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const filteredMovies = useMemo(() => {
    let result = activeCategory === 'all' ? [...movies] : movies.filter((m) => m.category === activeCategory);

    switch (sortBy) {
      case 'new':
        result.sort((a, b) => b.year - a.year || (b.isNew ? 1 : -1));
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'hot':
      default:
        result.sort((a, b) => (b.isHot ? 1 : 0) - (a.isHot ? 1 : 0) || b.rating - a.rating);
        break;
    }
    return result;
  }, [activeCategory, sortBy]);

  const categoryName = categoryNameMap[activeCategory] || '全部影视';
  const pageTitle = categoryTitleMap[activeCategory] || '全部影视资源 - 八戒影院';
  const pageDesc = categoryDescMap[activeCategory] || '八戒影院汇聚海量最新影视资源，高清在线流畅播放。';

  return (
    <>
      <SchemaOrg
        type="WebPage"
        pageTitle={pageTitle}
        pageDescription={pageDesc}
        pageUrl={`/category/${activeCategory}`}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 md:pt-24 pb-8">
        <div className="w-full px-4 md:px-6">
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: categoryName },
          ]} />

          <h1 className="mt-4 text-2xl md:text-3xl font-heading font-bold text-foreground-900">
            {categoryName}
          </h1>
          <p className="mt-2 text-sm text-foreground-600 max-w-3xl">
            {pageDesc}
          </p>

          <div className="mt-6 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-950'
                      : 'bg-background-100 text-foreground-600 hover:text-primary-400 border border-background-300/40'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="md:ml-auto flex items-center gap-2">
              <span className="text-sm text-foreground-600 whitespace-nowrap">排序：</span>
              <div className="flex items-center bg-background-100 rounded-full p-0.5">
                {([
                  { key: 'hot', label: '热门' },
                  { key: 'new', label: '最新' },
                  { key: 'rating', label: '高分' },
                ] as const).map((item) => (
                  <button
                    key={item.key}
                    onClick={() => setSortBy(item.key)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                      sortBy === item.key
                        ? 'bg-primary-500 text-background-950'
                        : 'text-foreground-600 hover:text-foreground-800'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 mt-6">
          {filteredMovies.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4" data-product-shop>
              {filteredMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-foreground-600">
              <i className="ri-movie-2-line text-5xl mb-4" />
              <p className="text-lg">暂无该分类的影视资源</p>
              <Link to="/category/all" className="mt-4 text-primary-500 hover:text-primary-400 transition-colors">
                查看全部资源
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}